import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Upload, RefreshCw, Loader2, CheckCircle2, XCircle, AlertCircle,
  Send, Download, Trash2, Eye, LifeBuoy, Copy,
} from "lucide-react";

const STATUS_META = {
  pending:  { color: "bg-muted text-muted-foreground",    Icon: AlertCircle,  label: "Pendiente" },
  sent:     { color: "bg-blue-500/15 text-blue-700",       Icon: Send,         label: "Enviado"   },
  approved: { color: "bg-success/15 text-success",         Icon: CheckCircle2, label: "Aprobado"  },
  rejected: { color: "bg-destructive/15 text-destructive", Icon: XCircle,      label: "Rechazado" },
  error:    { color: "bg-amber-500/15 text-amber-700",     Icon: AlertCircle,  label: "Error"     },
};

export default function ACECFCertificationTab() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportData, setSupportData] = useState(null);
  const [supportFilter, setSupportFilter] = useState("rejected");
  const [supportExpanded, setSupportExpanded] = useState({});
  const [fecha, setFecha] = useState("");
  const fechaActual = cases[0]?.raw?.FechaHoraAprobacionComercial || "";

  const loadCases = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dgii/acecf/cases");
      setCases(data.cases || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadCases(); }, []);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/dgii/acecf/upload-set", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.total} casos ACECF cargados`);
      await loadCases();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const sendAll = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/dgii/acecf/send-all");
      showSendResult(data);
      await loadCases();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const showSendResult = (data) => {
    if (!data.stopped_reason) { toast.success(`${data.ok}/${data.total} ACECF aprobados por DGII`); return; }
    toast.error(`Envío detenido para evitar más rechazos — ${data.stopped_reason}`, { duration: 20000 });
    if (data.fecha_corregida) toast.info(
      `Fecha de aprobación corregida a ${data.fecha_corregida} (valor que DGII espera). Todos quedaron pendientes: vuelve a pulsar "Enviar todos los pendientes".`,
      { duration: 20000 });
  };

  const applyFecha = async () => {
    try {
      const { data } = await api.post("/dgii/acecf/set-fecha-aprobacion", { fecha });
      toast.success(`Fecha ${data.fecha} aplicada a ${data.updated} casos (todos pendientes)`);
      setFecha(""); await loadCases();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const retryRejected = async () => {
    if (!window.confirm("Re-firmar y reenviar los ACECF rechazados o con error?")) return;
    setSending(true);
    try {
      const r1 = await api.post("/dgii/acecf/retry-rejected");
      toast.info(`${r1.data.cleared} marcados como pendientes`);
      const { data } = await api.post("/dgii/acecf/send-all");
      showSendResult(data);
      await loadCases();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const clearSet = async () => {
    if (!window.confirm("Borrar todos los casos ACECF? (La configuración DGII no se toca)")) return;
    try {
      await api.delete("/dgii/acecf/cases");
      toast.success("Set ACECF borrado");
      await loadCases();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadZip = async () => {
    try {
      const res = await api.get("/dgii/acecf/download-all", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `acecf-set-${new Date().toISOString().slice(0,16).replace(/:/g,"")}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openSupport = async () => {
    setSupportOpen(true);
    try {
      const { data } = await api.get("/dgii/acecf/support/trace");
      setSupportData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyXml = async (xml, encf) => {
    if (!xml) { toast.error("Aún no firmado"); return; }
    try {
      await navigator.clipboard.writeText(xml);
      toast.success(`XML de ${encf} copiado`);
    } catch { toast.error("No se pudo copiar"); }
  };

  const downloadXml = (xml, encf) => {
    if (!xml) { toast.error("Aún no firmado"); return; }
    const blob = new Blob([xml], { type: "text/xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${encf}.xml`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const stats = cases.reduce((acc, c) => {
    const s = c.status || "pending";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold">Paso 3: Pruebas de Aprobaciones Comerciales</h3>
          <p className="text-sm text-muted-foreground">
            Sube el Excel descargado del portal DGII (11 ACECF). El sistema firma
            cada uno y lo envía a <code className="text-xs">/CerteCF/AprobacionComercial</code>.
            DGII responde estado síncrono (no usa TrackId).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className="inline-flex">
            <input
              type="file" accept=".xlsx" className="hidden"
              onChange={onUpload}
              disabled={uploading}
              data-testid="acecf-upload-input"
            />
            <Button size="sm" variant="outline" asChild disabled={uploading}>
              <span>
                {uploading
                  ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  : <Upload className="w-3.5 h-3.5 mr-1" />}
                Subir Excel ACECF
              </span>
            </Button>
          </label>
          <Button
            size="sm" onClick={sendAll}
            disabled={sending || cases.length === 0 || !cases.some(c => ["pending","error","rejected"].includes(c.status))}
            data-testid="acecf-send-all-btn"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Enviar todos los pendientes
          </Button>
          <Button
            size="sm" variant="outline" onClick={retryRejected}
            disabled={sending || !cases.some(c => ["rejected","error"].includes(c.status))}
            data-testid="acecf-retry-rejected-btn"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reintentar rechazados
          </Button>
          <Button size="sm" variant="outline" onClick={downloadZip} disabled={cases.length === 0}>
            <Download className="w-3.5 h-3.5 mr-1" /> ZIP firmados
          </Button>
          <Button size="sm" variant="outline" onClick={openSupport} disabled={cases.length === 0} data-testid="acecf-support-btn">
            <LifeBuoy className="w-3.5 h-3.5 mr-1" /> Soporte DGII
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSet} disabled={cases.length === 0}>
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Limpiar
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="acecf-fecha-box">
        <span className="text-muted-foreground">Fecha/hora de aprobación enviada:</span>
        <b data-testid="acecf-fecha-actual">{fechaActual || "—"}</b>
        <Input
          value={fecha} onChange={(e) => setFecha(e.target.value)}
          placeholder="dd-MM-aaaa HH:mm:ss" className="h-8 w-52"
          data-testid="acecf-fecha-input"
        />
        <Button size="sm" variant="outline" onClick={applyFecha} disabled={!fecha || cases.length === 0} data-testid="acecf-fecha-apply-btn">
          Aplicar a todos
        </Button>
        <span className="text-xs text-muted-foreground">Debe ser igual a la del portal DGII; si DGII la rechaza, Amelia la corrige sola.</span>
      </div>

      {/* Resumen */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">Total: <b className="ml-1">{cases.length}</b></Badge>
        {Object.entries(stats).map(([k, v]) => {
          const meta = STATUS_META[k] || STATUS_META.pending;
          return (
            <Badge key={k} variant="outline" className={`${meta.color}`}>
              {meta.label}: <b className="ml-1">{v}</b>
            </Badge>
          );
        })}
      </div>

      {/* Lista */}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando casos…
        </div>
      )}

      {!loading && cases.length === 0 && (
        <div className="text-center py-10 border border-dashed rounded-md text-sm text-muted-foreground">
          Sube el Excel de Aprobaciones Comerciales desde el portal DGII (Paso 3).
        </div>
      )}

      {!loading && cases.length > 0 && (
        <div className="border rounded-md overflow-auto max-h-[600px]">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 sticky top-0">
              <tr className="text-left">
                <th className="px-3 py-2">#</th>
                <th className="px-3 py-2">eNCF</th>
                <th className="px-3 py-2">RNC Emisor</th>
                <th className="px-3 py-2">Estado target</th>
                <th className="px-3 py-2">Estado DGII</th>
                <th className="px-3 py-2">Mensaje</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((c) => {
                const meta = STATUS_META[c.status] || STATUS_META.pending;
                const Icon = meta.Icon;
                return (
                  <tr key={c.id} className="border-t" data-testid={`acecf-row-${c.encf}`}>
                    <td className="px-3 py-2">{c.idx}</td>
                    <td className="px-3 py-2 font-mono">{c.encf}</td>
                    <td className="px-3 py-2 font-mono">{c.rnc_emisor}</td>
                    <td className="px-3 py-2">
                      {c.estado_target === "1" ? "Aprobar" :
                       c.estado_target === "2" ? "Rechazar" :
                       c.estado_target === "3" ? "Aprob. c/comentarios" : c.estado_target}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-destructive">{c.last_error || ""}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Soporte Dialog */}
      <Dialog open={supportOpen} onOpenChange={setSupportOpen}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle>Soporte DGII — ACECF</DialogTitle>
          </DialogHeader>
          {!supportData ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3 text-xs">
                <span className="text-muted-foreground">Total: <b>{supportData.total}</b></span>
                {Object.entries(supportData.totals || {}).map(([k, v]) => (
                  <Badge key={k} variant="outline" className="text-[10px] font-mono">
                    {k}: {v}
                  </Badge>
                ))}
                <div className="ml-auto flex gap-1.5">
                  <Button size="sm" variant={supportFilter === "all" ? "default" : "outline"} onClick={() => setSupportFilter("all")}>Todos</Button>
                  <Button size="sm" variant={supportFilter === "rejected" ? "default" : "outline"} onClick={() => setSupportFilter("rejected")}>Rechazados</Button>
                  <Button size="sm" variant={supportFilter === "approved" ? "default" : "outline"} onClick={() => setSupportFilter("approved")}>Aprobados</Button>
                </div>
              </div>
              <div className="overflow-auto max-h-[65vh] space-y-2 pr-1">
                {(supportData.cases || [])
                  .filter((c) => supportFilter === "all" ? true :
                    supportFilter === "rejected" ? ["rejected", "error"].includes(c.status) :
                    c.status === "approved")
                  .map((c) => {
                    const expanded = !!supportExpanded[c.id];
                    const meta = STATUS_META[c.status] || STATUS_META.pending;
                    const Icon = meta.Icon;
                    const isReject = ["rejected", "error"].includes(c.status);
                    return (
                      <div key={c.id} className={`border rounded-md ${isReject ? "border-destructive/40 bg-destructive/5" : "border-border bg-card"}`}>
                        <div className="flex items-center gap-2 p-2.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${meta.color}`}>
                            <Icon className="w-3 h-3" /> {meta.label}
                          </span>
                          <span className="font-mono text-xs">{c.encf}</span>
                          <div className="ml-auto flex gap-1">
                            <Button size="sm" variant="outline" onClick={() => copyXml(c.signed_xml, c.encf)} disabled={!c.signed_xml}>
                              <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => downloadXml(c.signed_xml, c.encf)} disabled={!c.signed_xml}>
                              <Download className="w-3.5 h-3.5 mr-1" /> XML
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setSupportExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))}>
                              {expanded ? "Ocultar" : "Detalle"}
                            </Button>
                          </div>
                        </div>
                        {c.last_error && (
                          <div className="px-3 pb-2 text-[11px] text-destructive font-medium">
                            <AlertCircle className="w-3 h-3 inline mr-1" />{c.last_error}
                          </div>
                        )}
                        {expanded && (
                          <div className="border-t border-border/60 p-3 space-y-2 text-[11px]">
                            {c.last_dgii_response && (
                              <div>
                                <div className="font-semibold mb-1">Respuesta DGII:</div>
                                <pre className="bg-muted/40 p-2 rounded overflow-auto max-h-[200px] font-mono whitespace-pre-wrap break-all">
                                  {JSON.stringify(c.last_dgii_response, null, 2)}
                                </pre>
                              </div>
                            )}
                            <div>
                              <div className="font-semibold mb-1">XML enviado ({c.signed_xml_size} bytes):</div>
                              <pre className="bg-muted/40 p-2 rounded overflow-auto max-h-[320px] font-mono whitespace-pre-wrap break-all">
                                {c.signed_xml || "(aún no firmado)"}
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
